import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Alert, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Stack, Stepper, Step, StepLabel, useTheme, IconButton, Divider
} from '@mui/material';
import {
  CheckCircle as PassIcon, Cancel as FailIcon, Search as InspectIcon,
  Build as FixIcon, Verified as VerifyIcon, Close as CloseIcon, Warning as WarnIcon
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';

interface OrphanGroup {
  groupKey: string;
  itemId: number;
  itemCode?: string;
  itemName?: string;
  itemNameAr?: string;
  unitId: number;
  unitName?: string;
  unitNameAr?: string;
  poolType: string;
  locationId: number;
  locationName?: string;
  locationNameAr?: string;
  totalQuantity: number;
  unitCost: number;
  movementIds: number[];
  sourceDocumentType?: string;
  sourceDocumentNumber?: string;
  sourceDocumentId?: number;
  sourceDocumentDate?: string;
  sourcePostedDate?: string;
  postedByName?: string;
  createdByName?: string;
  movementCreatedDate?: string;
  rootCauseStory?: string;
  rootCauseStoryAr?: string;
  existingPlaceholderFound?: boolean;
  existingPlaceholderLocation?: string;
}

interface DetectResult {
  checkedAt: string;
  totalOrphanMovements: number;
  totalGroups: number;
  totalUnitsAffected: number;
  groups: OrphanGroup[];
}

interface InspectCheck {
  checkName: string;
  checkNameAr: string;
  passed: boolean;
  detail?: string;
}

interface InspectGroup {
  groupKey: string;
  itemId: number;
  itemName?: string;
  itemNameAr?: string;
  checks: InspectCheck[];
  allPassed: boolean;
}

interface InspectResult {
  allPassed: boolean;
  totalGroups: number;
  passedGroups: number;
  groups: InspectGroup[];
  warnings: string[];
}

interface FixDetail {
  itemId: number;
  itemCode?: string;
  itemName?: string;
  itemNameAr?: string;
  poolDescription: string;
  beforeValue: string;
  afterValue: string;
  newStockLevelId?: number;
  quantityRestored: number;
}

interface ApplyResult {
  isSuccess: boolean;
  message?: string;
  messageAr?: string;
  itemsFixed: number;
  totalUnitsRestored: number;
  details: FixDetail[];
  operationLog: string[];
  finalOrphanCount: number;
  completedAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  isRTL: boolean;
  onFixComplete?: () => void;
}

const OrphanBatchFixDialog: React.FC<Props> = ({ open, onClose, isRTL, onFixComplete }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [stage, setStage] = useState<number>(0); // 0 Detect, 1 Inspect, 2 Apply, 3 Result
  const [loading, setLoading] = useState(false);
  const [detect, setDetect] = useState<DetectResult | null>(null);
  const [inspect, setInspect] = useState<InspectResult | null>(null);
  const [apply, setApply] = useState<ApplyResult | null>(null);

  useEffect(() => {
    if (open) {
      setStage(0); setDetect(null); setInspect(null); setApply(null);
      runDetect();
    }
  }, [open]);

  const runDetect = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<DetectResult>('/DataIntegrity/orphan-batches/detect');
      setDetect(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل الفحص' : 'Detect failed'));
    } finally { setLoading(false); }
  };

  const runInspect = async () => {
    if (!detect) return;
    setLoading(true);
    try {
      const movementIds = detect.groups.flatMap(g => g.movementIds);
      const { data } = await axiosInstance.post<InspectResult>('/DataIntegrity/orphan-batches/inspect', { movementIds });
      setInspect(data);
      setStage(1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل التحقق' : 'Inspect failed'));
    } finally { setLoading(false); }
  };

  const runApply = async () => {
    if (!detect) return;
    setLoading(true);
    try {
      const movementIds = detect.groups.flatMap(g => g.movementIds);
      const { data } = await axiosInstance.post<ApplyResult>('/DataIntegrity/orphan-batches/apply', { movementIds });
      setApply(data);
      setStage(2);
      if (data.isSuccess) {
        toast.success(isRTL ? data.messageAr || 'نجح الإصلاح' : data.message || 'Fix succeeded');
        if (onFixComplete) onFixComplete();
      } else {
        toast.error(isRTL ? data.messageAr || 'فشل الإصلاح' : data.message || 'Fix failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل التنفيذ' : 'Apply failed'));
    } finally { setLoading(false); }
  };

  const renderDetect = () => {
    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!detect) return null;
    if (detect.totalOrphanMovements === 0) {
      return <Alert severity="success" icon={<PassIcon />}>{isRTL ? '🎉 لا توجد دفعات يتيمة — النظام نظيف' : '🎉 No orphan batches — system clean'}</Alert>;
    }
    return (
      <Stack spacing={2}>
        <Alert severity="warning" icon={<WarnIcon />}>
          {isRTL
            ? `وجدنا ${detect.totalOrphanMovements} حركة يتيمة في ${detect.totalGroups} مجموعة، بإجمالي ${detect.totalUnitsAffected} وحدة فعلية في DB بدون رصيد مقابل.`
            : `Found ${detect.totalOrphanMovements} orphan movements in ${detect.totalGroups} groups, totaling ${detect.totalUnitsAffected} actual units in DB with no matching stock level.`}
        </Alert>
        {detect.groups.map((g) => (
          <Paper key={g.groupKey} elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #f44336' }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip label={g.itemCode || `Item ${g.itemId}`} size="small" color="primary" />
                <Typography variant="h6">{isRTL ? g.itemNameAr : g.itemName}</Typography>
                <Chip label={`${g.totalQuantity} ${isRTL ? g.unitNameAr : g.unitName}`} size="small" color="warning" />
                <Chip label={`${g.unitCost.toFixed(4)} KWD`} size="small" variant="outlined" />
              </Box>
              <Typography variant="body2"><strong>{isRTL ? 'الموقع:' : 'Location:'}</strong> {g.poolType === 'Branch' ? '🏢' : '🏬'} {isRTL ? g.locationNameAr : g.locationName} (#{g.locationId})</Typography>
              {g.sourceDocumentNumber && (
                <Typography variant="body2"><strong>{isRTL ? 'المستند المصدر:' : 'Source:'}</strong> {g.sourceDocumentNumber} ({g.sourceDocumentType})</Typography>
              )}
              {g.postedByName && (
                <Typography variant="body2"><strong>{isRTL ? 'رحَّل:' : 'Posted by:'}</strong> {g.postedByName} {g.sourcePostedDate && `· ${new Date(g.sourcePostedDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}`}</Typography>
              )}
              {g.movementCreatedDate && (
                <Typography variant="body2"><strong>{isRTL ? 'حركات أُنشئت في:' : 'Movements created:'}</strong> {new Date(g.movementCreatedDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
              )}
              <Typography variant="body2"><strong>{isRTL ? 'حركات IDs:' : 'Movement IDs:'}</strong> {g.movementIds.join(', ')}</Typography>
              <Divider />
              <Box sx={{ bgcolor: isDark ? 'rgba(255,193,7,0.08)' : '#fffbeb', p: 1.5, borderRadius: 1, borderLeft: '3px solid #ffa726' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{isRTL ? '🔍 السبب الجذري:' : '🔍 Root Cause:'}</Typography>
                <Typography variant="body2">{isRTL ? g.rootCauseStoryAr : g.rootCauseStory}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderInspect = () => {
    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!inspect) return null;
    return (
      <Stack spacing={2}>
        <Alert severity={inspect.allPassed ? 'success' : 'error'} icon={inspect.allPassed ? <PassIcon /> : <FailIcon />}>
          {isRTL
            ? `${inspect.passedGroups}/${inspect.totalGroups} مجموعة اجتازت فحوصات السلامة`
            : `${inspect.passedGroups}/${inspect.totalGroups} groups passed safety checks`}
        </Alert>
        {inspect.groups.map((g) => (
          <Paper key={g.groupKey} elevation={1} sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${g.allPassed ? '#4caf50' : '#f44336'}` }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {g.allPassed ? '✅' : '❌'} {isRTL ? g.itemNameAr : g.itemName} (#{g.itemId})
            </Typography>
            <Stack spacing={0.5}>
              {g.checks.map((c, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1}>
                  {c.passed ? <PassIcon sx={{ color: '#4caf50', fontSize: 18 }} /> : <FailIcon sx={{ color: '#f44336', fontSize: 18 }} />}
                  <Typography variant="body2">{isRTL ? c.checkNameAr : c.checkName}</Typography>
                  {c.detail && <Typography variant="caption" color="text.secondary">— {c.detail}</Typography>}
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderApply = () => {
    if (loading) return <Box display="flex" flexDirection="column" alignItems="center" p={4} gap={2}><CircularProgress /><Typography>{isRTL ? 'جارٍ تنفيذ الإصلاح...' : 'Applying fix...'}</Typography></Box>;
    if (!apply) return null;
    return (
      <Stack spacing={2}>
        <Alert severity={apply.isSuccess ? 'success' : 'error'} icon={apply.isSuccess ? <VerifyIcon /> : <FailIcon />}
          sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="h6">{apply.isSuccess ? (isRTL ? '✅ تم الإصلاح بنجاح' : '✅ Fix Applied Successfully') : (isRTL ? '❌ فشل الإصلاح' : '❌ Fix Failed')}</Typography>
          <Typography variant="body2">{isRTL ? apply.messageAr : apply.message}</Typography>
        </Alert>

        {apply.isSuccess && apply.details.length > 0 && (
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: '8px 8px 0 0' }}>
              <Typography variant="subtitle1" fontWeight="bold">{isRTL ? 'جدول قبل/بعد الإصلاح' : 'Before/After Fix Table'}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الموقع' : 'Location'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#d32f2f' }}>{isRTL ? 'قبل' : 'Before'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{isRTL ? 'بعد' : 'After'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الكمية المستردة' : 'Qty Restored'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apply.details.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell>{d.itemCode} — {isRTL ? d.itemNameAr : d.itemName}</TableCell>
                      <TableCell>{d.poolDescription}</TableCell>
                      <TableCell sx={{ color: '#d32f2f' }}>{d.beforeValue}</TableCell>
                      <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{d.afterValue}</TableCell>
                      <TableCell><Chip label={d.quantityRestored} size="small" color="success" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(76,175,80,0.1)' : '#f1f8e9', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2"><strong>{isRTL ? 'إجمالي البنود المُصلَحة:' : 'Items Fixed:'}</strong> {apply.itemsFixed}</Typography>
              <Typography variant="body2"><strong>{isRTL ? 'إجمالي الوحدات المستردة:' : 'Units Restored:'}</strong> {apply.totalUnitsRestored}</Typography>
              <Typography variant="body2"><strong>{isRTL ? 'يتامى متبقية:' : 'Remaining Orphans:'}</strong> <Chip size="small" label={apply.finalOrphanCount} color={apply.finalOrphanCount === 0 ? 'success' : 'warning'} /></Typography>
            </Box>
          </Paper>
        )}

        {apply.operationLog.length > 0 && (
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#f5f5f5' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{isRTL ? '📋 سجل العمليات' : '📋 Operation Log'}</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxHeight: 200, overflow: 'auto' }}>
              {apply.operationLog.map((line, i) => <Box key={i}>{line}</Box>)}
            </Box>
          </Paper>
        )}
      </Stack>
    );
  };

  const steps = isRTL
    ? ['الكشف عن الخلل', 'فحوصات السلامة', 'تأكيد الإصلاح', 'النتيجة']
    : ['Detect', 'Inspect', 'Apply', 'Result'];

  const currentStep = apply ? 3 : inspect ? (stage === 1 ? 1 : 2) : 0;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <FixIcon /> {isRTL ? '🔧 إصلاح الدفعات اليتيمة' : '🔧 Fix Orphan Batches'}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} disabled={loading}><CloseIcon /></IconButton>
      </DialogTitle>
      <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label, i) => <Step key={i}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      </Box>
      <DialogContent sx={{ minHeight: 300 }}>
        {stage === 0 && renderDetect()}
        {stage === 1 && renderInspect()}
        {stage === 2 && renderApply()}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {stage === 0 && detect && detect.totalOrphanMovements > 0 && (
          <>
            <Button onClick={onClose} disabled={loading}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant="contained" startIcon={<InspectIcon />} onClick={runInspect} disabled={loading}>{isRTL ? 'فحص للتأكد' : 'Inspect & Verify'}</Button>
          </>
        )}
        {stage === 1 && inspect && (
          <>
            <Button onClick={() => setStage(0)} disabled={loading}>{isRTL ? 'رجوع' : 'Back'}</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<FixIcon />}
              onClick={runApply}
              disabled={loading || !inspect.allPassed}
              sx={inspect.allPassed ? { background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' } : undefined}
            >
              {isRTL ? '✅ تأكيد الإصلاح' : '✅ Confirm Fix'}
            </Button>
          </>
        )}
        {stage === 2 && (
          <Button variant="contained" onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>
        )}
        {(stage === 0 && (!detect || detect.totalOrphanMovements === 0)) && (
          <Button onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrphanBatchFixDialog;
